import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import AuthService from '../../services/authService';
import { Clock, Calendar, User, MapPin, Check, X, RefreshCw, Phone, Mail, Search, ChevronDown } from 'react-feather';
import AgentLayout from './AgentLayout';
import PropertyDetailsModal from '../common/PropertyDetailsModal';
import BuyerDetailsModal from '../common/BuyerDetailsModal';
import './AgentDashboard.css';

const AgentShowingsTab = ({ activeTab, onDashboard, onMyListings, onAddProperty, onLogout, onInvitations, onShowings }) => {
  const [showings, setShowings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedShowing, setSelectedShowing] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [statusFilter, setStatusFilter] = useState('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPropertyDetails, setShowPropertyDetails] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showBuyerDetailsModal, setShowBuyerDetailsModal] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [openStatusMenuId, setOpenStatusMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [timeSlots] = useState([
    '9:00', '9:30', '10:00', '10:30', '11:00', '11:30', 
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
  ]);


  useEffect(() => {
    fetchShowings();
  }, [statusFilter,]);

  // Close status dropdown on outside click
  useEffect(() => {
    if (!openStatusMenuId) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-status-menu]')) {
        setOpenStatusMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openStatusMenuId]);
  
  // Add this function after fetchShowings
  const showDebugInfo = (showing) => {
    console.log("Showing property data:", showing.property);
    console.log("Property type value:", showing.property?.propertyType);
    // Check all property keys
    console.log("All property keys:", showing.property ? Object.keys(showing.property) : []);
    return null;
  }

  const fetchShowings = async () => {
    setLoading(true);
    try {
      let url = 'https://back-end-x7tx.onrender.com/api/showings';
      
      // Only use direct API filtering for simple status filters
      if (statusFilter !== 'all' && statusFilter !== 'upcoming') {
        url += `?status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Filter the showings if using the 'upcoming' filter
      let showingsData = data.data || [];
      
      if (statusFilter === 'upcoming') {
        // Filter to only include 'pending' and 'confirmed' showings
        showingsData = showingsData.filter(showing => 
          showing.status === 'scheduled' || showing.status === 'confirmed'
        );
      }
      
      setShowings(showingsData);
    } catch (err) {
      console.error('Error fetching showings:', err);
      setError(`Failed to fetch showings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmShowing = async (showingId) => {
    try {
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/showings/${showingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ status: 'confirmed' }) // Changed from { status: 'scheduled', confirmed: true }
      });
  
      if (!response.ok) {
        throw new Error('Failed to confirm showing');
      }
  
      // Update local state
      setShowings(showings.map(showing => 
        showing._id === showingId ? { ...showing, status: 'confirmed' } : showing
      ));
      
      alert('Showing confirmed successfully!');
      fetchShowings();
    } catch (err) {
      console.error('Error confirming showing:', err);
      alert(`Failed to confirm showing: ${err.message}`);
    }
  };

  const handleDeclineShowing = async (showingId) => {
    if (!window.confirm('Are you sure you want to decline this showing?')) {
      return;
    }
    
    try {
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/showings/${showingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ status: 'cancelled', cancelReason: 'Declined by agent' })
      });

      if (!response.ok) {
        throw new Error('Failed to decline showing');
      }

      alert('Showing declined successfully');
      fetchShowings();
    } catch (err) {
      console.error('Error declining showing:', err);
      alert(`Failed to decline showing: ${err.message}`);
    }
  };

  const openRescheduleModal = (showing) => {
    setSelectedShowing(showing);
    const showingDate = new Date(showing.date);
    const formattedDate = showingDate.toISOString().split('T')[0];
    setRescheduleDate(formattedDate);
    setRescheduleTime(showing.timeSlot);
    setShowRescheduleModal(true);
  };

  const handleRescheduleShowing = async (e) => {
    e.preventDefault();
    
    if (!selectedShowing) return;
    
    try {
      // Create a date object that preserves the selected date regardless of timezone
      // First, parse the date string components
      const [year, month, day] = rescheduleDate.split('-').map(Number);
      
      // Create a date string that explicitly includes the timezone
      // This ensures the date stays as selected regardless of server timezone
      const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00.000Z`;
      
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/showings/${selectedShowing._id}/reschedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          date: dateString,
          timeSlot: rescheduleTime,
          notes: `Rescheduled by agent from ${selectedShowing.timeSlot} on ${new Date(selectedShowing.date).toLocaleDateString()}`
        })
      });
  
      if (!response.ok) {
        throw new Error('Failed to reschedule showing');
      }
  
      setShowRescheduleModal(false);
      alert('Showing rescheduled successfully');
      fetchShowings();
    } catch (err) {
      console.error('Error rescheduling showing:', err);
      alert(`Failed to reschedule showing: ${err.message}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeSlot) => {
    if (!timeSlot) return '';
    const [hourStr, minStr] = String(timeSlot).split(':');
    const hour = parseInt(hourStr, 10);
    const min = minStr || '00';
    if (isNaN(hour)) return timeSlot;
    if (hour === 0) return `12:${min} AM`;
    if (hour < 12) return `${hour}:${min} AM`;
    if (hour === 12) return `12:${min} PM`;
    return `${hour - 12}:${min} PM`;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phone;
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchShowings();
  };

  const filteredShowings = showings.filter(showing => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const buyerName = showing.user?.name?.toLowerCase() || '';
    const buyerEmail = showing.user?.email?.toLowerCase() || '';
    const propertyAddress = showing.property?.address?.toLowerCase() || '';
    
    return buyerName.includes(searchLower) || 
           buyerEmail.includes(searchLower) || 
           propertyAddress.includes(searchLower);
  });

  return (
    <div>
      <style>{`
        /* Light mode styling for Agent Showings */
        .section-title {
          color: #111 !important;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 20px;
          text-align: left;
          border: none !important;
          border-bottom: none !important;
          padding-bottom: 0 !important;
          letter-spacing: -0.01em;
        }
        .section-title::before,
        .section-title::after {
          display: none !important;
          content: none !important;
        }
        .filter-container {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03) !important;
          border-radius: 14px;
          padding: 20px !important;
          margin-bottom: 20px;
        }
        .grid-3-cols {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }
        .form-label {
          color: #6b7280 !important;
          font-size: 0.72rem !important;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-weight: 600;
          margin-bottom: 6px;
        }
        .form-control {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          color: #111 !important;
          border-radius: 9px !important;
          padding: 10px 12px !important;
          font-size: 0.9rem !important;
          transition: border-color 0.12s !important;
        }
        .form-control:focus {
          border-color: #111 !important;
          background-color: #ffffff !important;
          box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.06) !important;
          outline: none !important;
        }
        .form-control option {
          background-color: #ffffff;
          color: #111;
        }
        .search-button {
          color: #6b7280 !important;
        }
        .search-button:hover {
          color: #111 !important;
        }
        .btn-refresh {
          background-color: #ffffff !important;
          color: #111 !important;
          border: 1px solid #e5e7eb !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
          padding: 9px 16px;
          border-radius: 9px;
          font-weight: 500;
          transition: all 0.12s;
        }
        .btn-refresh:hover {
          background-color: #f9fafb !important;
          border-color: #d1d5db !important;
        }
        .showings-grid {
          display: grid;
          gap: 16px;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
        }
        table, tbody, thead, tr, td, th {
          background-color: transparent !important;
          color: #111 !important;
        }
        .table, .data-table, .showings-table {
          background-color: transparent !important;
          color: #111 !important;
        }
        .table th, .data-table th {
          color: #6b7280 !important;
          font-weight: 600;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .table td, .data-table td {
          color: #111 !important;
        }
        .table-responsive {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 14px;
          padding: 20px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }
        .showing-card {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 14px;
          padding: 18px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
          transition: all 0.15s;
        }
        .showing-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
          border-color: #d1d5db;
        }
        .showing-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 14px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f0f0f0;
        }
        .property-address {
          color: #111 !important;
          font-weight: 600;
          font-size: 1rem;
          transition: color 0.12s;
          letter-spacing: -0.005em;
        }
        .property-address:hover {
          color: #6b7280 !important;
        }
        .buyer-cell, .buyer-info, .buyer-name, .buyer-contact {
          color: #111 !important;
        }
        .buyer-name {
          color: #111 !important;
          font-weight: 600;
          font-size: 0.92rem;
        }
        .buyer-contact {
          color: #6b7280 !important;
          font-size: 0.82rem;
        }
        .buyer-contact a {
          color: #6b7280 !important;
        }
        .buyer-contact a:hover {
          color: #111 !important;
        }
        .property-price, .price-cell, .price {
          color: #111 !important;
          font-weight: 600;
        }
        span.price {
          color: #111 !important;
        }
        .status-badge {
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .status-scheduled {
          background-color: #fefce8;
          color: #854d0e;
          border: 1px solid #fde68a;
        }
        .status-confirmed {
          background-color: #ecfdf5;
          color: #047857;
          border: 1px solid #a7f3d0;
        }
        .status-completed {
          background-color: #f3f4f6;
          color: #4b5563;
          border: 1px solid #e5e7eb;
        }
        .status-cancelled {
          background-color: #fef2f2;
          color: #b91c1c;
          border: 1px solid #fecaca;
        }
        .showing-info {
          margin-bottom: 12px;
        }
        .info-row {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #4b5563;
          margin-bottom: 8px;
          font-size: 0.88rem;
        }
        .info-row svg {
          color: #9ca3af;
        }
        .date-time {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 12px;
          margin-bottom: 6px;
          background-color: #fafafa;
          border-radius: 9px;
          font-weight: 600;
          font-size: 0.9rem;
          color: #111 !important;
          border: 1px solid #f0f0f0;
        }
        .date-time .icon {
          color: #6b7280 !important;
        }
        .showing-notes {
          color: #6b7280 !important;
          margin-top: 8px;
        }
        .showing-notes small {
          color: #6b7280 !important;
        }
        .showing-actions {
          display: flex;
          gap: 8px;
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px solid #f0f0f0;
        }
        .btn-action {
          flex: 1;
          padding: 8px 12px;
          border-radius: 9px;
          font-size: 0.84rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.12s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          background-color: #ffffff !important;
          color: #111 !important;
          border: 1px solid #e5e7eb !important;
        }
        .btn-confirm {
          background-color: #111 !important;
          color: #ffffff !important;
          border: 1px solid #111 !important;
          font-weight: 600;
          transition: all 0.12s;
        }
        .btn-confirm:hover {
          background-color: #000 !important;
          border-color: #000 !important;
          transform: translateY(-1px);
        }
        .btn-confirm:active {
          transform: translateY(0);
        }
        .btn-reschedule,
        .btn-cancel {
          background-color: #ffffff !important;
          color: #111 !important;
          border: 1px solid #e5e7eb !important;
          transition: all 0.12s;
        }
        .btn-reschedule:hover,
        .btn-cancel:hover,
        .btn-action:hover {
          background-color: #f9fafb !important;
          border-color: #d1d5db !important;
        }
        .btn-cancel:hover {
          color: #b91c1c !important;
          border-color: #fecaca !important;
          background-color: #fef2f2 !important;
        }
        .empty-state {
          background-color: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 48px;
          text-align: center;
          color: #6b7280;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }
        .loading-spinner {
          border: 3px solid #e5e7eb !important;
          border-top-color: #111 !important;
        }
        .alert-danger {
          background-color: #fef2f2 !important;
          color: #b91c1c !important;
          border: 1px solid #fecaca !important;
          border-radius: 9px !important;
          padding: 11px 14px !important;
          font-weight: 500 !important;
          font-size: 0.875rem !important;
        }
        .modal-overlay {
          background-color: rgba(0, 0, 0, 0.2) !important;
          backdrop-filter: blur(4px);
        }
        .modal-dialog {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 16px !important;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18) !important;
        }
        .modal-header h3 {
          color: #111 !important;
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .modal-body {
          color: #111 !important;
        }
        .modal-body label {
          color: #6b7280 !important;
        }
        .btn-primary {
          background-color: #111 !important;
          color: #ffffff !important;
          border: 1px solid #111 !important;
          border-radius: 9px !important;
          padding: 9px 18px !important;
          font-weight: 600 !important;
          transition: all 0.12s !important;
        }
        .btn-primary:hover {
          background-color: #000 !important;
          border-color: #000 !important;
        }
        .btn-secondary {
          background-color: #ffffff !important;
          color: #111 !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 9px !important;
          padding: 9px 18px !important;
          font-weight: 500 !important;
          transition: all 0.12s !important;
        }
        .btn-secondary:hover {
          background-color: #f9fafb !important;
          border-color: #d1d5db !important;
        }
      `}</style>
    <>



      {error && (
        <div className="alert alert-danger mb-4">
          {error}
        </div>
      )}

      {/* Filters Section */}
      <div className="filter-container p-4 mb-4">
        <div className="grid-3-cols">
          {/* Search */}
          <div className="form-group">
            <form onSubmit={handleSearchSubmit}>
              <label className="form-label">Search by User or Property</label>
              <div className="search-container">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="search-button">
                  <Search size={16} />
                </button>
              </div>
            </form>
          </div>
          
          {/* Status Filter */}
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-control"
            >
              <option value="all">All Showings</option>
              <option value="upcoming">Upcoming</option>
              <option value="scheduled">Requested</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Showings Table */}
      {loading ? (
        <div className="text-center py-4">
          <div className="loading-spinner"></div>
          <p className="mt-2 text-text-medium">Loading showings...</p>
        </div>
      ) : filteredShowings.length === 0 ? (
        <div className="content-container text-center py-4">
          <p className="text-text-medium">No showings found{searchQuery ? ' matching your search criteria' : ''}.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Status</th>
                <th>Property</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              {filteredShowings.map((showing) => (
                <tr key={showing._id} className={showing.status === 'cancelled' ? 'cancelled-row' : ''}>
                  <td className="showing-cell">
                    <div className="showing-info">
                      <div className="date-time">
                        <Calendar size={14} className="icon" />
                        {formatDate(showing.date)}
                      </div>
                      <div className="date-time">
                        <Clock size={14} className="icon" />
                        {formatTime(showing.timeSlot)}
                      </div>
                      {showing.notes && (
                        <div className="showing-notes">
                          <small>{showing.notes}</small>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="status-cell" style={{ position: 'relative', overflow: 'visible' }}>
                    <div data-status-menu style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        type="button"
                        className={`status-badge ${
                          showing.status === 'cancelled' 
                            ? 'status-cancelled' 
                            : showing.status === 'completed' 
                              ? 'status-completed' 
                              : showing.status === 'confirmed'
                                ? 'status-confirmed' 
                                : 'status-scheduled'
                        }`}
                        onClick={(e) => {
                          if (openStatusMenuId === showing._id) {
                            setOpenStatusMenuId(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setMenuPosition({ top: rect.bottom + 6, left: rect.left });
                            setOpenStatusMenuId(showing._id);
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          border: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          transition: 'all 0.12s'
                        }}
                      >
                        {showing.status === 'scheduled' ? 'Requested' : (showing.status.charAt(0).toUpperCase() + showing.status.slice(1))}
                        <ChevronDown size={11} style={{ opacity: 0.7 }} />
                      </button>
                      <div style={{
                        marginTop: '12px',
                        fontSize: '0.68rem',
                        color: '#9ca3af',
                        fontStyle: 'italic',
                        letterSpacing: '0.01em'
                      }}>
                        Click badge to change status
                      </div>
                      {openStatusMenuId === showing._id && createPortal(
                        <div data-status-menu style={{
                          position: 'fixed',
                          top: menuPosition.top,
                          left: menuPosition.left,
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                          minWidth: '150px',
                          zIndex: 999999,
                          padding: '4px',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          {showing.status === 'scheduled' && (
                            <button
                              type="button"
                              onClick={() => {
                                setOpenStatusMenuId(null);
                                handleConfirmShowing(showing._id);
                              }}
                              disabled={showing.confirmed}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: '9px 12px',
                                borderRadius: '7px',
                                fontSize: '0.85rem',
                                fontWeight: '500',
                                color: showing.confirmed ? '#9ca3af' : '#111',
                                cursor: showing.confirmed ? 'not-allowed' : 'pointer',
                                textAlign: 'left'
                              }}
                              onMouseEnter={(e) => {
                                if (!showing.confirmed) e.currentTarget.style.backgroundColor = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {showing.confirmed ? 'Confirmed' : 'Confirm'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setOpenStatusMenuId(null);
                              openRescheduleModal(showing);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: '9px 12px',
                              borderRadius: '7px',
                              fontSize: '0.85rem',
                              fontWeight: '500',
                              color: '#111',
                              cursor: 'pointer',
                              textAlign: 'left'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            Reschedule
                          </button>
                          {showing.status !== 'cancelled' && showing.status !== 'completed' && (
                            <button
                              type="button"
                              onClick={() => {
                                setOpenStatusMenuId(null);
                                handleDeclineShowing(showing._id);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: '9px 12px',
                                borderRadius: '7px',
                                fontSize: '0.85rem',
                                fontWeight: '500',
                                color: '#b91c1c',
                                cursor: 'pointer',
                                textAlign: 'left'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              Decline
                            </button>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>
                  </td>
                  <td className="property-cell">
                    <div 
                      className="property-brief"
                      onClick={() => {
                        setSelectedProperty(showing.property);
                        setShowPropertyDetails(true);
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        cursor: 'pointer',
                        padding: '6px 8px',
                        marginLeft: '-8px',
                        borderRadius: '10px',
                        transition: 'background-color 0.12s',
                        alignItems: 'flex-start'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fafafa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img
                          src={showing.property?.mainImage || showing.property?.photos?.[0] || showing.property?.images?.[0] || 'https://via.placeholder.com/80x60?text=No+Image'}
                          alt={showing.property?.address || 'Property'}
                          style={{
                            width: '110px',
                            height: '80px',
                            borderRadius: '10px',
                            objectFit: 'cover',
                            flexShrink: 0,
                            border: '1px solid #f0f0f0'
                          }}
                        />
                        <div className="property-specs" style={{ fontSize: '0.88rem', color: '#111', fontWeight: '600', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <span className="price">
                            {showing.property?.price 
                              ? (typeof showing.property.price === 'string' && showing.property.price.startsWith('$') 
                                  ? showing.property.price 
                                  : `$${showing.property.price.toLocaleString()}`)
                              : 'N/A'}
                          </span>
                          <span style={{ fontSize: '0.8rem', fontWeight: '500', color: '#4b5563' }}>
                            {showing.property?.bedrooms || 0} bd <span style={{ color: '#d1d5db', margin: '0 4px' }}>•</span> {showing.property?.bathrooms || 0} ba
                          </span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'left', width: '100%' }}>
                        <div 
                          className="property-address"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '260px',
                            fontSize: '0.88rem'
                          }}
                        >
                          {showing.property?.address || 'N/A'}
                        </div>
                        {showing.property?.csz && (
                          <div style={{
                            color: '#6b7280',
                            fontSize: '0.76rem',
                            marginTop: '2px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '260px'
                          }}>
                            {showing.property.csz}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="buyer-cell">
                    <div 
                      onClick={() => {
                        setSelectedBuyer(showing.user);
                        setShowBuyerDetailsModal(true);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        padding: '6px 8px',
                        marginLeft: '-8px',
                        borderRadius: '10px',
                        transition: 'background-color 0.12s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fafafa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {(showing.user?.profilePhoto || showing.user?.photo || showing.user?.profilePicture || showing.user?.profileImage || showing.user?.avatar || showing.user?.image) ? (
                        <img
                          src={showing.user.profilePhoto || showing.user.photo || showing.user.profilePicture || showing.user.profileImage || showing.user.avatar || showing.user.image}
                          alt={showing.user?.name || 'User'}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            flexShrink: 0,
                            border: '1px solid #f0f0f0'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            if (e.currentTarget.nextSibling) {
                              e.currentTarget.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        display: (showing.user?.profilePhoto || showing.user?.photo || showing.user?.profilePicture || showing.user?.profileImage || showing.user?.avatar || showing.user?.image) ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        flexShrink: 0,
                        border: '1px solid #f0f0f0'
                      }}>
                        {(showing.user?.name?.charAt(0) || 'A').toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0, textAlign: 'left' }}>
                        <div className="buyer-name" style={{ textAlign: 'left' }}>
                          {showing.user?.name || 'Anonymous'}
                        </div>
                        {showing.user?.phone && (
                          <div className="buyer-contact" style={{ marginTop: '2px' }}>
                            <a 
                              href={`tel:${showing.user.phone}`} 
                              className="contact-link"
                              onClick={(e) => e.stopPropagation()}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                            >
                              <Phone size={12} />
                              {formatPhoneNumber(showing.user.phone)}
                            </a>
                          </div>
                        )}
                        {showing.user?.email && (
                          <div className="buyer-contact" style={{ marginTop: '2px' }}>
                            <a 
                              href={`mailto:${showing.user.email}`} 
                              className="contact-link"
                              onClick={(e) => e.stopPropagation()}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                            >
                              <Mail size={12} />
                              <span style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '180px',
                                display: 'inline-block',
                                verticalAlign: 'middle'
                              }}>
                                {showing.user.email}
                              </span>
                            </a>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            color: '#111',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '0.72rem',
                            fontWeight: '600'
                          }}>
                            {showing.user?.likes?.length || 0} liked
                          </span>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            color: '#111',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '0.72rem',
                            fontWeight: '600'
                          }}>
                            {showing.user?.dislikes?.length || 0} passed
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedShowing && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <div className="modal-header">
              <h3>Reschedule Showing</h3>
              <button className="modal-close" onClick={() => setShowRescheduleModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleRescheduleShowing}>
                <div className="form-group">
                  <label className="form-label">Property:</label>
                  <div className="form-static-text">
                    {selectedShowing.property ? selectedShowing.property.address : 'Address not available'}
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Buyer:</label>
                  <div className="form-static-text">
                    {selectedShowing.user?.name || selectedShowing.user?.email || 'Anonymous'}
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="rescheduleDate" className="form-label">New Date:</label>
                  <input
                    type="date"
                    id="rescheduleDate"
                    className="form-control"
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="rescheduleTime" className="form-label">New Time:</label>
                  <select
                    id="rescheduleTime"
                    className="form-control"
                    value={rescheduleTime}
                    onChange={(e) => setRescheduleTime(e.target.value)}
                    required
                  >
                    {timeSlots.map(slot => (
                      <option key={slot} value={slot}>
                        {formatTime(slot)}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowRescheduleModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                  >
                    Reschedule Showing
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Property Details Modal */}
      <PropertyDetailsModal
        property={selectedProperty}
        isOpen={showPropertyDetails}
        onClose={() => {
          setShowPropertyDetails(false);
          setSelectedProperty(null);
        }}
      />

      {/* Buyer Details Modal */}
      <BuyerDetailsModal
        buyer={selectedBuyer}
        isOpen={showBuyerDetailsModal}
        onClose={() => {
          setShowBuyerDetailsModal(false);
          setSelectedBuyer(null);
        }}
      />
    </>
    </div>
  );
};

export default AgentShowingsTab;